import { ComponentRef, Directive, OnDestroy, Type } from '@angular/core';
import { DecoratorService } from './decorator.service';
import { IDecoratedComponent } from './decorated-component.interface';
import { DecoratorConfig } from './decorator-config';
import { ComponentHostDirective } from './component-host.directive';
import { DynamicComponentService } from './dynamic-component.service';
import { IShortLogger, Log } from '../services/logger.service';

/**
 * Enhanced base class for components that work with the decoration system.
 * Provides comprehensive functionality for loading components from the decoration registry
 * and managing their lifecycle.
 *
 * @typeParam TConfig - Type extending DecorationConfig for component configuration
 */
@Directive()
export abstract class DecoratedComponentBase<TConfig extends DecoratorConfig> implements OnDestroy {
  // Track active component references for cleanup
  protected activeComponentRefs: ComponentRef<any>[] = [];

  // Map hosts to their respective component references
  protected hostComponentMap: Map<ComponentHostDirective, ComponentRef<any>> = new Map();

  constructor(
    protected decorationService: DecoratorService,
    protected dynamicComponentService: DynamicComponentService,
    protected configKey: string,
    protected readonly logger: IShortLogger<DecoratedComponentBase<TConfig>>
  ) {
  }

  /**
   * Load a component from the decoration registry into a component host
   *
   * @param host The ComponentHostDirective to load the component into
   * @param componentName The name of the component to load
   * @param inputs Optional inputs to set on the component
   * @returns ComponentRef to the loaded component or null if not found
   */
  protected loadComponentFromRegistry<T = IDecoratedComponent>(
    host: ComponentHostDirective,
    componentName: string,
    inputs?: { [key: string]: any }
  ): ComponentRef<T> | null {
    try {
      // Get all components with the specified decoration
      const decoratedComponents = this.decorationService.getDecoratedComponents(this.configKey);

      // Find component by name
      const componentType = decoratedComponents.find(comp => {
        const config = this.decorationService.getComponentConfig<TConfig>(comp, this.configKey);
        return config?.name === componentName || comp.name === componentName;
      });

      if (!componentType) {
        this.logger.e( `Component not found in decoration registry: ${componentName}`);
        return null;
      }

      return this.loadComponentByType(host, componentType as unknown as Type<T>, inputs);
    } catch (error) {
      this.logger.e( `Error loading component from registry: ${componentName}`, error);
      return null;
    }
  }

  /**
   * Load a component by type into a host
   *
   * @param host The ComponentHostDirective to load the component into
   * @param componentType The component type to load
   * @param inputs Optional inputs to set on the component
   * @returns ComponentRef to the loaded component
   */
  protected loadComponentByType<T>(
    host: ComponentHostDirective,
    componentType: Type<T>,
    inputs?: { [key: string]: any }
  ): ComponentRef<T> | null {
    try {
      // Clear any existing component in this host
      this.clearComponentHost(host);

      // Load the component
      const componentRef = host.loadComponent<T>(componentType, inputs);

      // Track for cleanup
      this.trackComponentRef(componentRef, host);

      return componentRef;
    } catch (error) {
      this.logger.e( `Error loading component by type: ${componentType.name}`, error);
      return null;
    }
  }

  /**
   * Get component configuration from the decoration registry
   *
   * @param componentName The name of the component
   * @returns The component configuration or undefined if not found
   */
  protected getComponentConfig(componentName: string): TConfig | undefined {
    const decoratedComponents = this.decorationService.getDecoratedComponents(this.configKey);
    const component = decoratedComponents.find(comp => {
      const config = this.decorationService.getComponentConfig<TConfig>(comp, this.configKey);
      return config?.name === componentName || comp.name === componentName;
    });

    return component ? this.decorationService.getComponentConfig<TConfig>(component, this.configKey) : undefined;
  }

  /**
   * Get all components with the configured decoration
   *
   * @returns Array of component types that have the specified decoration
   */
  protected getDecoratedComponents(): Type<IDecoratedComponent>[] {
    return this.decorationService.getDecoratedComponents(this.configKey);
  }

  /**
   * Get all configurations for the specified decoration type
   *
   * @returns Array of configurations
   */
  protected getAllConfigs(): TConfig[] {
    const components = this.decorationService.getDecoratedComponents(this.configKey);
    return components.map(comp =>
      this.decorationService.getComponentConfig<TConfig>(comp, this.configKey)
    ).filter(config => config !== undefined) as TConfig[];
  }

  /**
   * Find components with specific configuration values
   *
   * @param predicate Function to test configuration values
   * @returns Array of matching component types
   */
  protected findComponentsWhere(predicate: (config: TConfig) => boolean): Type<IDecoratedComponent>[] {
    return this.decorationService.findComponentsWhere<TConfig>(this.configKey, predicate);
  }

  /**
   * Clear a specific component host
   *
   * @param host The host to clear
   */
  protected clearComponentHost(host: ComponentHostDirective): void {
    if (host) {
      // Get the component ref associated with this host
      const componentRef = this.hostComponentMap.get(host);

      // Destroy the component
      if (componentRef) {
        this.dynamicComponentService.destroyComponent(componentRef);
        this.activeComponentRefs = this.activeComponentRefs.filter(ref => ref !== componentRef);
        this.hostComponentMap.delete(host);
      }

      // Clear the host
      host.clear();
    }
  }

  /**
   * Clear all tracked component hosts
   */
  protected clearAllComponentHosts(): void {
    // Get all unique hosts
    const hosts = Array.from(this.hostComponentMap.keys());

    // Clear each host
    hosts.forEach(host => this.clearComponentHost(host));
  }

  /**
   * Clean up all component references on destruction
   */
  ngOnDestroy(): void {
    // Clear all hosts
    this.clearAllComponentHosts();

    // Destroy any remaining component references
    this.activeComponentRefs.forEach(ref => {
      this.dynamicComponentService.destroyComponent(ref);
    });

    this.activeComponentRefs = [];
    this.hostComponentMap.clear();
  }

  /**
   * Add a component reference to track for cleanup
   *
   * @param componentRef The component reference to track
   * @param host Optional associated host directive
   */
  protected trackComponentRef<T>(componentRef: ComponentRef<T>, host?: ComponentHostDirective): void {
    this.activeComponentRefs.push(componentRef);

    if (host) {
      this.hostComponentMap.set(host, componentRef);
    }
  }
}
